/**
 * JobSyncNudge — DEC-328 "Ask driver to sync" on the job detail modal.
 *
 * Shown only for a job that can be sitting on a driver's phone (SYNC_NUDGE_STATUS_CODES,
 * the mirror of the API's list in services/sync-nudge.ts), with an assigned driver, to a
 * role in SYNC_NUDGE_ROLES. Reads the job's latest nudge from GET /v1/jobs/:id/sync-nudges
 * (key under ['jobs'], so the button's invalidation refreshes it) and renders the same
 * SyncNudgeButton the stuck list uses.
 */

import * as React from 'react';
import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { SyncNudgeButton } from './SyncNudgeButton';
import { canSyncNudgeRole } from './stuck-jobs-format';

/** Mirror of SYNC_NUDGE_STATUS_CODES in packages/api/src/services/sync-nudge.ts. */
export const SYNC_NUDGE_STATUS_CODES: ReadonlySet<string> = new Set([
  'EN_ROUTE_TO_PICKUP',
  'ARRIVED_PICKUP',
  'ONSITE_WORK_START',
  'ONSITE_WORK_END',
  'EN_ROUTE_TO_DROP_OFF',
  'DISPOSAL_IN',
  'UNLOADING_START',
  'DISPOSAL_OUT',
  'PAUSED',
]);

interface SyncNudgeHistory {
  readonly nudges: ReadonlyArray<{
    readonly id: string;
    readonly sentAt: string;
    readonly firstCheckinAfterAt: string | null;
  }>;
}

export interface JobSyncNudgeProps {
  readonly jobId: string;
  readonly statusCode: string;
  readonly driverId: string | null;
  readonly role: string;
}

export function JobSyncNudge({
  jobId,
  statusCode,
  driverId,
  role,
}: JobSyncNudgeProps): React.JSX.Element | null {
  const show =
    driverId !== null && SYNC_NUDGE_STATUS_CODES.has(statusCode) && canSyncNudgeRole(role);
  const apiOpts = useApiOpts();
  const { data, dataUpdatedAt } = useApiQuery<SyncNudgeHistory>(
    apiOpts,
    `/v1/jobs/${jobId}/sync-nudges`,
    { queryKey: ['jobs', 'sync-nudges', jobId], enabled: show },
  );
  if (!show) return null;
  const latest = data?.nudges[0];
  const nowMs = dataUpdatedAt > 0 ? Math.max(dataUpdatedAt, Date.now()) : Date.now();
  return (
    <SyncNudgeButton
      jobId={jobId}
      lastNudgeAt={latest?.sentAt ?? null}
      lastNudgeCheckinAt={latest?.firstCheckinAfterAt ?? null}
      nowMs={nowMs}
    />
  );
}
